/**
 * PlayoffBracket - Phase 1.5 §5.1
 * Routes: /league/:id/playoffs
 * Renders bracket for PRE_PLAYOFFS / ACTIVE / COMPLETE statuses.
 * Always reachable; never 404s during regular season.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Trophy, ChevronRight, Crown } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { authHeaders } from "../contexts/AuthContext";
import SeasonShell from "../components/season/SeasonShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROUND_LABEL = {
  // 6-team: weeks 15/16/17 → Wild Card / Semifinal / Championship
  // 4-team: 15/16 → Round 1 / Championship
  // 8-team: 15/16/17 → Quarterfinal / Semifinal / Championship
};

function roundLabel(playoffTeams, weekIndex) {
  // weekIndex 0 = first playoff week
  if (playoffTeams === 4) {
    return weekIndex === 0 ? "Round 1" : "Championship";
  }
  if (playoffTeams === 6) {
    return ["Wild Card", "Semifinal", "Championship"][weekIndex] || `Round ${weekIndex + 1}`;
  }
  if (playoffTeams === 8) {
    return ["Quarterfinal", "Semifinal", "Championship"][weekIndex] || `Round ${weekIndex + 1}`;
  }
  return `Round ${weekIndex + 1}`;
}

function MatchupCard({ m, isChampion, isDark, isCompact }) {
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  const homeIsWinner = m.winner_team_id === m.home.team_id;
  const awayIsWinner = m.winner_team_id === m.away.team_id;
  const isFinal = m.status === "FINAL";

  const TeamRow = ({ side, isWinner }) => (
    <div
      className={`flex items-center justify-between py-2 px-3 ${
        isWinner ? "bg-[#FF6B00]/15 border-l-4 border-[#FF6B00]" : ""
      }`}
      data-testid={`bracket-team-${side.team_id}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {side.seed && (
          <span className={`flex-shrink-0 w-5 h-5 rounded text-[10px] font-display font-black flex items-center justify-center ${
            isWinner ? "bg-[#FF6B00] text-white" : "bg-[#FF6B00]/20 text-[#FF6B00]"
          }`}>
            {side.seed}
          </span>
        )}
        <span className={`font-display font-bold uppercase text-xs truncate ${isWinner ? "text-[#FF6B00]" : fg}`}>
          {side.team_name}
        </span>
        {isChampion && isWinner && <Crown size={12} className="text-[#FFD700] flex-shrink-0" />}
      </div>
      <span className={`font-display font-black text-sm ${isWinner ? "text-[#FF6B00]" : muted}`}>
        {isFinal ? side.score.toFixed(1) : "-"}
      </span>
    </div>
  );

  return (
    <div
      className={`rounded-xl ${cardBg} border ${cardBorder} overflow-hidden ${
        isChampion ? "ring-2 ring-[#FFD700]/60 shadow-lg shadow-[#FFD700]/10" : ""
      } ${isCompact ? "min-w-[200px]" : "min-w-[240px]"}`}
      data-testid={`bracket-matchup-${m.matchup_id}`}
    >
      <TeamRow side={m.home} isWinner={homeIsWinner} />
      <div className={`h-px ${isDark ? "bg-[#2A2A2A]" : "bg-[#E0E0E0]"}`} />
      <TeamRow side={m.away} isWinner={awayIsWinner} />
    </div>
  );
}

export default function PlayoffBracket() {
  const { id: leagueId } = useParams();
  const { isDark } = useTheme();

  const [bracket, setBracket] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [showConsolation, setShowConsolation] = useState(false);

  useEffect(() => {
    setLoadError(null);
    axios.get(`${API}/playoffs/${leagueId}/bracket`, { headers: authHeaders() })
      .then((r) => setBracket(r.data))
      .catch((e) => setLoadError(e?.response?.data?.detail || e?.message || "Failed to load bracket"));
  }, [leagueId]);

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  if (!bracket) {
    if (loadError) {
      return (
        <SeasonShell>
          <div className="py-20 text-center" data-testid="bracket-load-error">
            <p className="font-display font-black uppercase text-xl text-[#FF4444] mb-2">Could not load bracket</p>
            <p className={`font-body text-sm ${muted} mb-4`}>{loadError}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white text-sm font-display font-bold uppercase tracking-wide"
              data-testid="bracket-retry"
            >Retry</button>
          </div>
        </SeasonShell>
      );
    }
    return <SeasonShell><div className="py-20 text-center"><span className="font-display font-black uppercase text-2xl text-[#FF6B00]">Loading bracket…</span></div></SeasonShell>;
  }

  const isPre = bracket.status === "PRE_PLAYOFFS";
  const isComplete = bracket.status === "COMPLETE";
  const championId = bracket.champion_team_id;

  // ── PRE_PLAYOFFS view ──
  if (isPre) {
    return (
      <SeasonShell>
        <div className="flex items-center gap-3 mb-2">
          <Trophy size={28} className="text-[#FF6B00]" />
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">PGF Playoffs</h1>
        </div>
        <p className={`text-sm ${muted} mb-6`}>
          Playoffs begin Week 15 - projected seeds based on current standings.
        </p>

        <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-5 mb-6`} data-testid="bracket-projected">
          <h2 className="font-display font-bold uppercase tracking-wide text-sm mb-4 text-[#FF6B00]">
            Projected Bracket Seeds ({bracket.playoff_teams} teams)
          </h2>
          <div className="space-y-2">
            {(bracket.projected_seeds || []).map((s) => (
              <Link
                key={s.team_id} to={`/league/${leagueId}/team/${s.team_id}`}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border ${cardBorder} hover:border-[#FF6B00]/60 transition-all`}
                data-testid={`projected-seed-${s.seed}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#FF6B00]/20 text-[#FF6B00] flex items-center justify-center font-display font-black text-sm">
                    {s.seed}
                  </span>
                  <div>
                    <span className={`font-display font-bold uppercase text-sm ${fg}`}>{s.team_name}</span>
                    <span className={`block text-[10px] ${muted}`}>{s.wins}-{s.losses}-{s.ties} · {s.points_for.toFixed(1)} PF</span>
                  </div>
                </div>
                <ChevronRight size={16} className={muted} />
              </Link>
            ))}
          </div>
        </div>

        <div className={`text-[11px] ${muted}`}>
          Seeds are recomputed each week. Final seeding locks at the end of the regular season.
        </div>
      </SeasonShell>
    );
  }

  // ── ACTIVE / COMPLETE bracket ──
  const rounds = bracket.rounds || [];
  const consRounds = bracket.consolation_rounds || [];

  // Identify championship matchup (last week's championship-bracket matchup with highest combined score)
  let champMatchupId = null;
  if (isComplete && rounds.length > 0) {
    const lastWeek = rounds[rounds.length - 1];
    if (lastWeek?.matchups?.length > 0) {
      const sorted = [...lastWeek.matchups].sort((a, b) =>
        (b.home.score + b.away.score) - (a.home.score + a.away.score)
      );
      champMatchupId = sorted[0]?.matchup_id;
    }
  }

  return (
    <SeasonShell>
      <div className="flex items-center gap-3 mb-2">
        <Trophy size={28} className="text-[#FF6B00]" />
        <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
          PGF Playoffs
        </h1>
        {isComplete && <Crown size={28} className="text-[#FFD700]" />}
      </div>
      <p className={`text-sm ${muted} mb-6`} data-testid="bracket-status-line">
        {isComplete ? "Season complete - champion crowned." : "Live bracket - winner advances each week."}
      </p>
      {isComplete && (
        <Link
          to={`/league/${leagueId}/recap`}
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-lg bg-[#FFD700]/20 hover:bg-[#FFD700]/30 border border-[#FFD700]/60 text-[#FFD700] font-display font-bold uppercase text-sm tracking-wide"
          data-testid="bracket-recap-cta"
        >
          <Crown size={14} /> View Season Recap
        </Link>
      )}

      {/* Bracket grid */}
      <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-5 mb-6 overflow-x-auto`}>
        <div className="flex gap-6 min-w-fit">
          {rounds.map((rnd, idx) => (
            <div key={rnd.week} className="flex flex-col gap-4 min-w-[240px] flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] font-display font-black mb-2">
                {roundLabel(bracket.playoff_teams, idx)} · Week {rnd.week}
              </div>
              {rnd.matchups.map((m) => (
                <MatchupCard
                  key={m.matchup_id}
                  m={m}
                  isChampion={m.matchup_id === champMatchupId}
                  isDark={isDark}
                />
              ))}
              {rnd.matchups.length === 0 && (
                <div className={`text-xs ${muted} italic`}>-</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Consolation bracket (collapsible) */}
      {bracket.consolation_enabled && consRounds.length > 0 && (
        <div className={`rounded-2xl ${cardBg} border ${cardBorder} mb-6 overflow-hidden`} data-testid="bracket-consolation">
          <button
            onClick={() => setShowConsolation(!showConsolation)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#FF6B00]/5 transition-all"
            data-testid="bracket-consolation-toggle"
          >
            <span className="font-display font-bold uppercase tracking-wide text-sm">
              Consolation Bracket
            </span>
            <ChevronRight size={16} className={`${muted} transition-transform ${showConsolation ? "rotate-90" : ""}`} />
          </button>
          {showConsolation && (
            <div className="px-5 pb-5 overflow-x-auto">
              <div className="flex gap-6 min-w-fit">
                {consRounds.map((rnd) => (
                  <div key={rnd.week} className="flex flex-col gap-4 min-w-[200px]">
                    <div className={`text-[10px] uppercase tracking-[0.3em] ${muted} font-display font-black mb-2`}>
                      Week {rnd.week}
                    </div>
                    {rnd.matchups.map((m) => (
                      <MatchupCard key={m.matchup_id} m={m} isDark={isDark} isCompact />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bracket Insights - derived in-page */}
      {rounds.length > 0 && !isPre && (
        <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-5`} data-testid="bracket-insights">
          <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-3 text-[#FF6B00]">Bracket Insights</h3>
          <ul className={`space-y-1.5 text-sm ${fg}`}>
            <li className="flex items-center gap-2">
              <Trophy size={14} className="text-[#FF6B00]" />
              {bracket.playoff_teams}-team format · {rounds.length} round{rounds.length === 1 ? "" : "s"} · {bracket.consolation_enabled ? "consolation on" : "no consolation"}
            </li>
            {isComplete && championId && (
              <li className="flex items-center gap-2">
                <Crown size={14} className="text-[#FFD700]" />
                Champion crowned. Visit recap for the receipts.
              </li>
            )}
          </ul>
        </div>
      )}
    </SeasonShell>
  );
}
